import { Icon } from '@iconify/react';
import { useState, useEffect, useRef } from 'react';
import { Inspection } from '../../services/inspection.service';
import { checklistService, Checklist } from '../../services/checklist.service';
import ViewSummary from './ViewSummary';
import { showSuccessMessage } from '../../helper/showMessage';
import { useDispatch } from 'react-redux';
import { actions } from '../../store/actions';

interface CriticalSummaryModalProps {
    onClose: () => void;
    inspection?: Inspection | null;
    onReturnToLog?: () => void;
    onInspectionUpdated?: () => void;
}

export default function CriticalSummaryModal({ onClose, inspection, onReturnToLog, onInspectionUpdated }: CriticalSummaryModalProps) {
    const dispatch = useDispatch();
    const [selectedOptions, setSelectedOptions] = useState<{ [key: number]: string; }>({});
    const [checklists, setChecklists] = useState<Checklist[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showViewSummary, setShowViewSummary] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [originalStatus, setOriginalStatus] = useState<{ [key: number]: string; }>({});
    const options = ['Acceptable', 'Fixed', 'Not Fixed'];

    // Use ref to track if data has been loaded to prevent re-fetching
    const dataLoadedRef = useRef(false);
    const inspectionIdRef = useRef<number | null>(null);

    useEffect(() => {
        // Only fetch data if inspection ID has changed or data hasn't been loaded yet
        if (inspection?.id && (inspectionIdRef.current !== inspection.id || !dataLoadedRef.current)) {
            inspectionIdRef.current = inspection.id;
            dataLoadedRef.current = true;
            fetchChecklists();
        }
    }, [inspection?.id]); // Only depend on inspection ID, not the entire inspection object

    const fetchChecklists = async () => {
        if (!inspection?.id) return;

        try {
            setLoading(true);
            setError(null);
            const data = await checklistService.getChecklistsByInspection(inspection.id);
            setChecklists(data);

            // Initialize selected options
            const initialOptions: { [key: number]: string; } = {};
            const originalOptions: { [key: number]: string; } = {};
            data.forEach(checklist => {
                const statusText = checklist.status === 'passed' ? 'Fixed' : checklist.status === 'acceptable' ? 'Acceptable' : 'Not Fixed';
                initialOptions[checklist.id] = statusText;
                originalOptions[checklist.id] = statusText;
            });
            setSelectedOptions(initialOptions);
            setOriginalStatus(originalOptions);
            setHasChanges(false);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to fetch checklist data');
            console.error('Failed to fetch checklists:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOptionChange = (checklistId: number, value: string) => {
        setSelectedOptions(prev => {
            const newOptions = {
                ...prev,
                [checklistId]: value
            };

            // Check if there are any changes from original
            const hasAnyChanges = Object.keys(newOptions).some(id =>
                newOptions[parseInt(id)] !== originalStatus[parseInt(id)]
            );
            setHasChanges(hasAnyChanges);

            return newOptions;
        });
    };

    const handleSave = async () => {
        if (!hasChanges || saving) return;

        try {
            setSaving(true);
            setError(null);

            // Find changed checklists and update them
            const updatePromises = Object.keys(selectedOptions).map(async (checklistIdStr) => {
                const checklistId = parseInt(checklistIdStr);
                const currentStatus = selectedOptions[checklistId];
                const originalStatusValue = originalStatus[checklistId];

                if (currentStatus !== originalStatusValue) {
                    const newStatus = currentStatus === 'Fixed' ? 'passed' : currentStatus === 'Acceptable' ? 'acceptable' : 'failed';
                    return await checklistService.updateChecklistStatus(checklistId, newStatus);
                }
                return null;
            });

            const results = await Promise.all(updatePromises);
            const completedResults = results.filter(result => result !== null);

            // Check if any inspection was completed
            const inspectionCompleted = completedResults.some(result => result?.inspectionCompleted);

            if (inspectionCompleted) {
                console.log('working')
                dispatch(actions.modelOpen(false))
                showSuccessMessage('Inspection completed! All checklists have been resolved.');
                // Notify parent components to refresh their data
                if (onInspectionUpdated) onInspectionUpdated();
                // Close the modal since inspection is completed
                handleClose();
                return;
            }

            // Refresh the data
            await fetchChecklists();

            // Notify parent components to refresh their data
            if (onInspectionUpdated) onInspectionUpdated();

        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to save changes');
            console.error('Failed to save changes:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        // Reset the data loaded flag so modal can work for future inspections
        dataLoadedRef.current = false;
        inspectionIdRef.current = null;
        onClose();
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'passed':
                return 'text-green';
            case 'acceptable':
                return 'text-orange-500';
            case 'failed':
                return 'text-[#F60000]';
            case 'in-progress':
                return 'text-blue';
            default:
                return 'text-gray-500';
        }
    };

    const getOptionColor = (option: string) => {
        switch (option) {
            case 'Fixed':
                return 'text-green bg-green-50 border-green-200';
            case 'Acceptable':
                return 'text-orange-600 bg-orange-50 border-orange-200';
            case 'Not Fixed':
                return 'text-red-600 bg-red-50 border-red-200';
            default:
                return 'text-gray-500 bg-gray-50 border-gray-200';
        }
    };

    const getStatusBadge = (option: string) => {
        switch (option) {
            case 'Fixed':
                return { icon: 'material-symbols:check-circle', color: 'text-green', bg: 'bg-green-100' };
            case 'Acceptable':
                return { icon: 'material-symbols:warning', color: 'text-orange-600', bg: 'bg-orange-100' };
            case 'Not Fixed':
                return { icon: 'material-symbols:error', color: 'text-red-600', bg: 'bg-red-100' };
            default:
                return { icon: 'material-symbols:help', color: 'text-gray-500', bg: 'bg-gray-100' };
        }
    };

    const formatAlertStatus = (alertStatus: string) => {
        switch (alertStatus) {
            case 'sent':
                return 'Yes';
            case 'failed':
                return 'Failed';
            case 'pending':
            default:
                return '--';
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center w-full p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-3">
                    <h2 className="text-black-1 text-xl font-[Geist-Bold]">Critical Event Summary</h2>
                    <button 
                        onClick={handleClose} 
                        className="w-8 h-8 rounded-full bg-gray-1 hover:bg-gray-2 flex items-center justify-center transition-colors"
                    >
                        <Icon icon="material-symbols:close" className="text-gray-2 text-lg" />
                    </button>
                </div>
                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="flex items-center gap-2 text-gray-2">
                                <Icon icon="eos-icons:loading" className="text-lg animate-spin" />
                                Loading checklist data...
                            </div>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-center">
                                <div className="text-red-500 mb-2">{error}</div>
                                <button 
                                    onClick={fetchChecklists} 
                                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-[Geist-Medium] transition-colors"
                                >
                                    Retry
                                </button>
                            </div>
                        </div>
                    ) : !inspection ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-gray-2">No inspection data available</div>
                        </div>
                    ) : (
                        /* Table */
                        <div className="p-6">
                            <div className="bg-white border border-gray-3 rounded-xl overflow-hidden">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-6">
                                            <th className="px-4 py-3 text-left text-gray font-[Geist-Medium] text-sm">Event</th>
                                            <th className="px-4 py-3 text-center text-gray font-[Geist-Medium] text-sm">Unit</th>
                                            <th className="px-4 py-3 text-center text-gray-2 font-[Geist-Medium] text-sm">Location</th>
                                            <th className="px-4 py-3 text-center text-gray-2 font-[Geist-Medium] text-sm">DOT</th>
                                            <th className="px-4 py-3 text-center text-gray font-[Geist-Medium] text-sm">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {checklists.map((checklist, index) => (
                                            <tr key={checklist.id} className={`border-t border-gray-3 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-6'}`}>
                                                <td className="px-4 py-3 text-black-1 font-[Geist-Medium] text-sm">
                                                    {checklist.name}
                                                </td>
                                                <td className="px-4 py-3 text-center text-black-1 font-[Geist-Medium] text-sm">
                                                    {inspection.trailerId}
                                                </td>
                                                <td className="px-4 py-3 text-center text-gray-2 font-[Geist-Medium] text-sm">
                                                    --
                                                </td>
                                                <td className="px-4 py-3 text-center text-gray-2 font-[Geist-Medium] text-sm">
                                                    {formatAlertStatus(checklist.alertStatus || 'pending')}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <div className="flex justify-center">
                                                        <div className="relative min-w-[120px]">
                                                            <select
                                                                value={selectedOptions[checklist.id] || (
                                                                    checklist.status === 'passed' ? 'Fixed' :
                                                                        checklist.status === 'acceptable' ? 'Acceptable' : 'Not Fixed'
                                                                )}
                                                                onChange={(e) => handleOptionChange(checklist.id, e.target.value)}
                                                                className={`w-full pl-8 pr-8 py-1.5 text-xs rounded-md border font-[Geist-Medium] focus:outline-none focus:ring-1 focus:ring-green-200 transition-all duration-200 appearance-none cursor-pointer hover:shadow-sm ${getOptionColor(selectedOptions[checklist.id] || (checklist.status === 'passed' ? 'Fixed' : checklist.status === 'acceptable' ? 'Acceptable' : 'Not Fixed'))}`}
                                                            >
                                                                {options.map((option) => (
                                                                    <option
                                                                        key={option}
                                                                        value={option}
                                                                        className="py-1"
                                                                    >
                                                                        {option}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                                <Icon icon="material-symbols:keyboard-arrow-down" className="text-gray-2 text-xs" />
                                                            </div>
                                                            {/* Status Badge */}
                                                            <div className="absolute left-1.5 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                                {(() => {
                                                                    const currentOption = selectedOptions[checklist.id] || (checklist.status === 'passed' ? 'Fixed' : checklist.status === 'acceptable' ? 'Acceptable' : 'Not Fixed');
                                                                    const badge = getStatusBadge(currentOption);
                                                                    return (
                                                                        <div className={`w-4 h-4 rounded-full ${badge.bg} flex items-center justify-center`}>
                                                                            <Icon icon={badge.icon} className={`${badge.color} text-xs`} />
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-3 bg-gray-6">
                    <div className="flex justify-between items-center">
                        <button
                            onClick={() => {
                                handleClose();
                                if (onReturnToLog) onReturnToLog();
                            }}
                            disabled={!inspection || (checklists.length > 0 && !checklists.some(checklist => checklist.status === 'failed'))}
                            className="px-6 py-2 text-gray-2 hover:text-black-1 text-sm font-[Geist-Medium] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {(!inspection || (checklists.length > 0 && !checklists.some(checklist => checklist.status === 'failed'))) ? 'Inspection Completed' : 'Back to Report'}
                        </button>

                        <div className="flex gap-3">
                            <button
                                onClick={handleSave}
                                disabled={!hasChanges || saving}
                                className="px-6 py-2 bg-green hover:bg-green-600 text-white rounded-lg text-sm font-[Geist-Medium] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {saving && <Icon icon="eos-icons:loading" className="text-sm animate-spin" />}
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>

                            <button
                                onClick={() => setShowViewSummary(true)}
                                className="px-6 py-2 bg-blue hover:bg-blue-1 text-white rounded-lg text-sm font-[Geist-Medium] transition-colors"
                            >
                                View Summary
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {
                showViewSummary &&
                <ViewSummary
                    onClose={() => setShowViewSummary(false)}
                    inspection={inspection}
                    onReturnToLog={() => {
                        setShowViewSummary(false);
                        // Stay in CriticalSummaryModal when returning from ViewSummary
                    }}
                />
            }
        </div>
    );
};